import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import userManagementSvc from "../services/api/userManagement";
import { notify } from "../common/notificationService";
import { CurrentUser } from "../models/types/user/current-user";
import { DEVICE_ID_KEY, TOKEN_KEY } from "../config/common";
import { getCookies } from "../utils/configuration";
import settingService from "../services/api/setting";

interface UserProviderProps {
    children: ReactNode;
}

interface UserContextType {
    userInfo: CurrentUser | null;
    isUse2FA: boolean;
    fetchUserInfo: () => Promise<void>;
    setUserInfo: React.Dispatch<React.SetStateAction<CurrentUser | null>>;
    setIsUse2FA: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    handleUnregisterNotification: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
    userInfo: null,
    isUse2FA: false,
    fetchUserInfo: async () => { },
    setUserInfo: () => { },
    setIsUse2FA: () => { },
    loading: false,
    setLoading: () => { },
    handleUnregisterNotification: async () => { },
});


export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [userInfo, setUserInfo] = useState<CurrentUser | null>(null);
    const [isUse2FA, setIsUse2FA] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const token = getCookies(TOKEN_KEY);

    const fetchUserInfo = async () => {
        setLoading(true);
        try {
            const response = await userManagementSvc.getAccountDetail();
            if (response.content) {
                setUserInfo(response.content);
                setIsUse2FA(response.content.isUse2FA);
            } else {
                notify('Lỗi', response.err, 'danger');
            }
        } catch (error) {
            notify('Lỗi', 'Xảy ra lỗi', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleUnregisterNotification = async () => {
        if (!token) return;
        try {
            const deviceId = getCookies(DEVICE_ID_KEY);
            if (!deviceId) return;
            await settingService.unregisterNotification(deviceId);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        if (token) {
            fetchUserInfo();
        }
    }, [token])

    return (
        <UserContext.Provider value={{ userInfo, isUse2FA, fetchUserInfo, setUserInfo, setIsUse2FA, loading, setLoading, handleUnregisterNotification }}>
            {children}
        </UserContext.Provider>
    )
}

export const useUserInfo = (): UserContextType => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export default UserContext;
